import React, { useEffect, useState } from 'react';
import ActivityHistoryList from '../ActivityHistoryList';
import ErrorMessage from '../ErrorMessage';
import LoadingSpinner from '../LoadingSpinner';
import { BTN_XS } from '../../constants/buttons';
import { useLatestRequest } from '../../hooks/useSearchRequest';
import { fetchPersonWeekHistory, fetchUnitHistory } from '../../utils/timesheetsApi';
import { unitNameIn } from '../../utils/timesheetDisplay';
import { HISTORY_FILTERS, filterHistory, personWeekHistoryEntries, unitHistoryEntries } from './timesheetHistory';

/**
 * THE "History" panel of the manager screen (owner-approved mock 2026-10-29, panels 4 and 5): one
 * person's week (`kind="person"`, with the mock's All · Times · Status · Unit edits filter) or one
 * unit's mass edits over the week (`kind="unit"`). It renders THE shared list
 * (`components/ActivityHistoryList.js`) over the words in `./timesheetHistory.js` — never a second
 * list. Manager-only: the routes refuse anyone else, and only the manager screen mounts this.
 *
 * 📐 It sits INLINE under the grid it describes, full width — on a phone that IS the full-width
 * panel the brief asks for, and at 1366 a seven-day grid leaves no room for a side column.
 * The list scrolls inside a capped height so a long history never pushes Save off the screen.
 *
 * `refreshSignal` — bump it after a save so an open panel re-reads (a counter: each save refetches).
 * Every response is gated on `useLatestRequest`: a slow read for the previous week or person can
 * never paint over the current one.
 */
export default function TimesheetHistoryPanel({
  kind, lineageId, weekStart, unitId, from, to, units = [], title, onClose, refreshSignal = 0, testId = 'timesheet-history',
}) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const begin = useLatestRequest();

  useEffect(() => {
    const isCurrent = begin();
    setError(null);
    const req = kind === 'unit' ? fetchUnitHistory(unitId, from, to) : fetchPersonWeekHistory(lineageId, weekStart);
    req.then((r) => {
      if (!isCurrent()) return;
      if (!r.ok) { setError((r.body && r.body.error) || 'Could not load the history.'); return; }
      setRows((r.body && r.body.entries) || []);
    }).catch(() => { if (isCurrent()) setError('Could not reach the server. Try again.'); });
  }, [kind, lineageId, weekStart, unitId, from, to, refreshSignal, begin]);

  const unitName = (id) => unitNameIn(units, id);
  const entries = rows == null ? [] : (kind === 'unit' ? unitHistoryEntries(rows, unitName) : personWeekHistoryEntries(rows, unitName));
  const shown = kind === 'unit' ? entries : filterHistory(entries, filter);

  return (
    <section aria-label={title} data-testid={testId}
      className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="mr-auto text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
        <button type="button" className={`${BTN_XS.secondary} max-md:min-h-[44px]`} onClick={onClose} data-testid={`${testId}-close`}>
          Close
        </button>
      </div>
      {error ? <ErrorMessage error={error} /> : rows == null ? (
        <div className="py-4"><LoadingSpinner /></div>
      ) : (
        <div className="max-h-[26rem] overflow-y-auto pr-1">
          <ActivityHistoryList
            entries={shown}
            {...(kind === 'unit' ? {} : { filters: HISTORY_FILTERS, activeFilter: filter, onFilterChange: setFilter, filtersAriaLabel: 'Show' })}
            emptyText={kind === 'unit' ? 'No unit edits this week.' : 'Nothing recorded for this week yet.'}
            testIdPrefix={testId}
          />
        </div>
      )}
    </section>
  );
}
